import React from 'react';

import useAnimation from '../../../hooks/useAnimation';
import useRequestAnimationFrame from '../../../hooks/useRequestAnimationFrame';
import { FontSize } from '../../../typings/design-tokens';
import { makeClassNames } from '../../../utils/styles';
import HorizontalBars from '../../atoms/horizontal-bars/horizontal-bars';
import Text from '../../atoms/text/text';

import './player-card-chart.scss';

const TITLE_FONT_SIZE: FontSize = '16';
const PERCENTAGE_FONT_SIZE: FontSize = '42';
const VALUE_FONT_SIZE: FontSize = '14';

type EventType = {
  text: string;
  value: number;
};

export interface IPlayerCardChart {
  title: string;
  success: EventType;
  failure: EventType;
}

const classNames = () =>
  makeClassNames({
    firstRow: 'player-card-chart__row',
    secondRow: ['player-card-chart__row', 'player-card-chart__row--margin-top'],
    textWrapper: 'player-card-chart__second-row__text-wrapper',
    title: 'player-card-chart__first-row__title',
    percentage: 'player-card-chart__first-row__percentage',
  });

// HELPERS
const fromNumberToString = (value: number) => (value / 1000).toFixed(1).replace(',', '.');

const PlayerCardChart = ({ title, success, failure }: IPlayerCardChart) => {
  const cn = classNames();
  const percentage: number =
    failure.value === 0 && success.value === 0 ? 0 : (success.value / (failure.value + success.value)) * 100;

  // ANIMATION
  const animationBegin: number = 500;
  const animationDuration: number = 1000;
  const horizontalBars = {
    animated: true,
    animationBegin: `${fromNumberToString(animationBegin)}s`,
    animationDuration: `${fromNumberToString(animationDuration)}s`,
    colorBar2: '#d6d6d6',
    valueStart: 0,
  };
  const { progress, animationWaitHandler, shouldAnimate } = useAnimation(animationDuration, animationBegin);
  useRequestAnimationFrame(animationWaitHandler, { shouldAnimate, duration: animationBegin + animationDuration });

  return (
    <div>
      <div className={cn.firstRow}>
        <Text color="blue" className={cn.title} fontSize={TITLE_FONT_SIZE} fontWeight="400">
          {title}
        </Text>
        <Text color="blue" className={cn.percentage} fontSize={PERCENTAGE_FONT_SIZE} fontWeight="500">
          {(percentage * progress).toFixed(0)}%
        </Text>
      </div>
      <div>
        <HorizontalBars valueBar1={success.value} valueBar2={failure.value} {...horizontalBars} />
      </div>
      <div className={cn.secondRow}>
        <div className={cn.textWrapper}>
          <Text color="blue" fontSize={VALUE_FONT_SIZE} fontWeight="500">
            {success.value}
          </Text>
          <Text color="blue" fontSize={VALUE_FONT_SIZE} fontWeight="400">
            {success.text}
          </Text>
        </div>
        <div className={cn.textWrapper}>
          <Text color="blue" fontSize={VALUE_FONT_SIZE} fontWeight="500">
            {failure.value}
          </Text>
          <Text color="blue" fontSize={VALUE_FONT_SIZE} fontWeight="400">
            {failure.text}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PlayerCardChart;
